import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, PencilLine, Save } from 'lucide-react'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { formatDateTime } from '../../lib/utils'
import { useLocale } from '../../i18n/LocaleProvider'

export function JobDetailsPanel({ job, onSaveRequirements, editable = true }) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftRequirements, setDraftRequirements] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmMode, setConfirmMode] = useState('')
  const [error, setError] = useState('')
  const { t } = useLocale()

  const originalRequirements = String(job?.requirements || '').trim()

  useEffect(() => {
    setDraftRequirements(originalRequirements)
    setEditing(false)
    setConfirmMode('')
    setError('')
  }, [job?.jobId, originalRequirements])

  const hasChanges = useMemo(
    () => draftRequirements.trim() !== originalRequirements,
    [draftRequirements, originalRequirements]
  )

  async function commitRequirements(rescoreCandidates) {
    if (typeof onSaveRequirements !== 'function') return

    setSaving(true)
    setError('')
    try {
      await onSaveRequirements({
        requirements: draftRequirements.trim(),
        rescoreCandidates,
      })
      setEditing(false)
      setConfirmMode('')
    } catch (err) {
      setError(err?.message || t('authenticated.jobDetailsPanel.updateFailed'))
    } finally {
      setSaving(false)
    }
  }

  function cancelEditing() {
    setDraftRequirements(originalRequirements)
    setEditing(false)
    setConfirmMode('')
    setError('')
  }

  if (!job) return null

  return (
    <div className="card overflow-hidden border border-gray-100">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('authenticated.jobDetailsPanel.title')}</p>
          <p className="text-xs text-gray-500">
            {open ? t('authenticated.jobDetailsPanel.hide') : t('authenticated.jobDetailsPanel.show')}
          </p>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          <div className="space-y-1">
            <p className="text-lg font-bold text-gray-900">{job.title || t('authenticated.jobDetailsPanel.untitled')}</p>
            {job.updatedAt && (
              <p className="text-xs text-gray-400">
                {t('authenticated.jobDetailsPanel.lastUpdated', { date: formatDateTime(job.updatedAt) })}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('authenticated.jobDetailsPanel.description')}</p>
            <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
              {job.description || t('authenticated.jobDetailsPanel.noDescription')}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t('authenticated.jobDetailsPanel.requirements')}
              </p>
              {editable && !editing && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditing(true)}
                  icon={<PencilLine className="w-4 h-4" />}
                >
                  {t('authenticated.jobDetailsPanel.editRequirements')}
                </Button>
              )}
            </div>

            {editing && editable ? (
              <div className="space-y-3">
                <textarea
                  className="input min-h-[140px] resize-y"
                  value={draftRequirements}
                  onChange={(event) => setDraftRequirements(event.target.value)}
                  placeholder={t('authenticated.jobDetailsPanel.placeholder')}
                />
                <p className="text-xs text-amber-700">
                  {t('authenticated.jobDetailsPanel.noAutoRescore')}
                </p>
                {error && <p className="text-xs text-red-600">{error}</p>}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="secondary" onClick={cancelEditing} disabled={saving}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setConfirmMode('rescore')}
                    loading={saving}
                    disabled={!hasChanges}
                    icon={<Save className="w-4 h-4" />}
                  >
                    {t('authenticated.jobDetailsPanel.saveRequirements')}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap rounded-xl border border-gray-100 bg-gray-50 p-3">
                {job.requirements || t('authenticated.jobDetailsPanel.noRequirements')}
              </p>
            )}
          </div>

          <div className="grid gap-3 text-xs text-gray-500 sm:grid-cols-2">
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="font-semibold text-gray-700">{t('authenticated.jobDetailsPanel.tip')}</p>
              <p className="mt-1">
                {t('authenticated.jobDetailsPanel.tipCopy')}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="font-semibold text-gray-700">{t('authenticated.jobDetailsPanel.control')}</p>
              <p className="mt-1">
                {t('authenticated.jobDetailsPanel.controlCopy')}
              </p>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={confirmMode === 'rescore'}
        onClose={() => !saving && setConfirmMode('')}
        title={t('authenticated.jobDetailsPanel.rescoreTitle')}
        size="sm"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {t('authenticated.jobDetailsPanel.rescoreDescription')}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => commitRequirements(false)}
              disabled={saving}
            >
              {t('authenticated.jobDetailsPanel.saveOnly')}
            </Button>
            <Button
              variant="primary"
              onClick={() => commitRequirements(true)}
              loading={saving}
            >
              {t('authenticated.jobDetailsPanel.saveAndRescore')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
